import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PauseCircle, Wallet, Clock, History, MapPin, QrCode, ChevronRight, CalendarRange, Play, Banknote } from "lucide-react";
import { toast } from "sonner";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { ErrorState } from "../../components/shared/ErrorState";
import { ConfirmDialog } from "../../components/shared/ConfirmDialog";
import { PauseResumeDialog } from "../../components/subscriptions/PauseResumeDialog";
import { formatDate, formatCurrency } from "../../lib/utils";
import { useTaxConfig, breakdownFromPayment } from "../../lib/tax";

function statusBadgeClasses(label) {
  if (label === "Active") return "bg-success-bg text-success";
  if (label === "Paused") return "bg-warning-bg text-warning";
  return "bg-danger-bg text-danger";
}

// Renewal nudge popup should not reappear on every return to Home — show at most
// once per 30 minutes (per customer).
const RENEW_NOTICE_WINDOW_MS = 30 * 60 * 1000;

export default function CustomerHome() {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: null, me: null });
  const [today, setToday] = useState(null);
  const [pauseBatch, setPauseBatch] = useState(null);
  const [busy, setBusy] = useState(null);
  const [pauseDialog, setPauseDialog] = useState(null); // "pause" | "resume" | null
  const [confirmPauseTomorrow, setConfirmPauseTomorrow] = useState(false);
  // Default hidden so the dialog never flashes before the 30-min throttle runs.
  const [expiryDismissed, setExpiryDismissed] = useState(true);
  const [pending, setPending] = useState([]);
  const taxCfg = useTaxConfig();

  const loadPauseBatch = useCallback(() => {
    apiClient.get("/customer/pause-batch").then(({ data }) => setPauseBatch(data)).catch(() => setPauseBatch(null));
  }, []);

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: null }));
    apiClient
      .get("/customer/me")
      .then(({ data }) => {
        setState({ loading: false, error: null, me: data });
        if (data.type === "2") {
          apiClient.get("/customer/deliveries/today").then(({ data: t }) => setToday(t));
        }
        loadPauseBatch();
        apiClient.get("/customer/payments")
          .then(({ data: p }) => setPending(p.filter((x) => x.payable)))
          .catch(() => setPending([]));
      })
      .catch((error) => setState({ loading: false, error: getApiErrorMessage(error, "Could not load your account."), me: null }));
  }, [loadPauseBatch]);

  useEffect(() => { load(); }, [load]);

  // Throttle the renewal popup: decide ONCE per screen mount. Open it only when
  // eligible AND it hasn't been shown in the last 30 minutes (persisted per
  // customer). Returning to Home within the window keeps it closed. The ref guard
  // stops a second `me` refresh from re-reading the just-written timestamp and
  // wrongly re-hiding the dialog.
  const renewNoticeDecided = useRef(false);
  useEffect(() => {
    const me = state.me;
    if (!me || renewNoticeDecided.current) return;
    const eligible = (me.tiffins_left != null && me.tiffins_left <= 3) || me.status === 4;
    if (!eligible) { setExpiryDismissed(true); return; }
    renewNoticeDecided.current = true;
    const key = `vt_renew_notice_shown_${me.customer_id}`;
    let last = 0;
    try { last = Number(localStorage.getItem(key)) || 0; } catch { /* ignore */ }
    if (Date.now() - last >= RENEW_NOTICE_WINDOW_MS) {
      try { localStorage.setItem(key, String(Date.now())); } catch { /* ignore */ }
      setExpiryDismissed(false);
    } else {
      setExpiryDismissed(true);
    }
  }, [state.me]);

  // Recovery after returning from Stripe Checkout (esp. the iOS Apple-Pay flow
  // where the browser-confirm page is skipped): re-verify this customer's
  // pending online payments against Stripe and refresh if any activated.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("verify") !== "1") return;
    apiClient
      .post("/customer/payments/verify-pending")
      .then(({ data }) => { if (data?.activated > 0) toast.success("Payment confirmed — your plan is active."); })
      .catch(() => {})
      .finally(() => { setSearchParams({}, { replace: true }); load(); });
  }, [searchParams, setSearchParams, load]);

  const pauseTomorrow = async () => {
    setBusy("pause");
    try {
      const { data } = await apiClient.post("/customer/pause-tomorrow");
      toast.success(`Tomorrow paused — new renewal date ${formatDate(data.new_end_date)}`);
      setConfirmPauseTomorrow(false);
      load();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not pause tomorrow."));
    } finally {
      setBusy(null);
    }
  };

  const recharge = async () => {
    setBusy("recharge");
    try {
      const { data } = await apiClient.post("/customer/recharge");
      navigate(`/pay/${data.payment_link.token}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not start payment."));
      setBusy(null);
    }
  };

  if (state.loading) {
    return (
      <div data-testid="customer-home-page">
        <Skeleton className="h-36 w-full rounded-2xl" />
        <Skeleton className="mt-3 h-20 w-full rounded-2xl" />
        <Skeleton className="mt-3 h-16 w-full rounded-2xl" />
      </div>
    );
  }

  if (state.error) {
    return <ErrorState message={state.error} onRetry={load} testId="customer-home-error" />;
  }

  const me = state.me;

  if (!me?.has_subscription) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-surface py-14 text-center" data-testid="customer-home-no-subscription">
        <p className="text-sm text-text-muted">You don't have a plan yet.</p>
        <Button className="mt-4 rounded-xl" onClick={() => navigate("/customer/register")} data-testid="customer-home-get-plan-button">
          Get a plan
        </Button>
      </div>
    );
  }

  const tiffinsDone = Math.max(0, (me.tiffins_total || 0) - (me.tiffins_left || 0));
  const pct = me.tiffins_total > 0 ? Math.min(100, Math.round((tiffinsDone / me.tiffins_total) * 100)) : 0;
  const renewEligible = (me.tiffins_left != null && me.tiffins_left <= 3) || me.status === 4;
  // Split pending payments: cash-selected ones are collected by the driver on
  // delivery (not an online "Pay now" debt), so they get a calm info banner.
  const onlinePending = pending.filter((p) => String(p.payment_method) !== "1");
  const cashPending = pending.filter((p) => String(p.payment_method) === "1");

  return (
    <div className="space-y-4" data-testid="customer-home-page">
      {/* Prominent Remaining Tiffins hero card */}
      <div className="relative overflow-hidden rounded-2xl bg-primary p-5 text-white shadow-md" data-testid="customer-home-tiffins-card">
        <p className="text-xs font-semibold uppercase tracking-wider text-white/60">{me.plan_name}</p>
        <p className="mt-1 font-heading text-4xl font-extrabold text-accent" data-testid="customer-home-tiffins-done">
          {tiffinsDone} <span className="text-sm font-normal text-white/70">of {me.tiffins_total} tiffins used</span>
        </p>
        {/* Filled portion = tiffins already taken; empty portion = tiffins left in the plan */}
        <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-white/20">
          <div className="h-full rounded-full bg-accent transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
        <p className="mt-1.5 text-right text-sm font-semibold text-white/80" data-testid="customer-home-tiffins-left">
          {me.tiffins_left} left
        </p>
        <p className="mt-2 flex items-center gap-1.5 text-xs text-white/70">
          <Clock size={13} strokeWidth={1.5} /> Expected renewal: {formatDate(me.end_date)}
        </p>
      </div>

      {/* Subscription status card */}
      <div className="flex items-center justify-between rounded-2xl border border-border bg-surface p-4 shadow-sm" data-testid="customer-home-status-card">
        <div>
          <p className="text-sm font-semibold text-text-main">Subscription Status</p>
          <p className="mt-0.5 text-xs text-text-muted">{formatDate(me.start_date)} — {formatDate(me.end_date)}</p>
        </div>
        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${statusBadgeClasses(me.status_label)}`} data-testid="customer-home-status-badge">
          {me.status_label}
        </span>
      </div>

      {/* Upcoming pause banner */}
      {pauseBatch?.has_pause && (
        <div className="flex items-center gap-2 rounded-2xl border border-warning/30 bg-warning-bg px-4 py-3 text-sm text-warning" data-testid="customer-home-pause-banner">
          <PauseCircle size={16} strokeWidth={1.75} />
          <span className="font-medium">Pause scheduled · {formatDate(pauseBatch.from_date)} → {formatDate(pauseBatch.to_date)}</span>
        </div>
      )}

      {/* Plan expiring soon — renew nudge */}
      {renewEligible && (
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-danger/30 bg-danger-bg px-4 py-3" data-testid="customer-home-expiry-banner">
          <span className="text-sm font-semibold text-danger">
            {me.status === 4 ? "Your plan has expired." : `Only ${me.tiffins_left} tiffin${me.tiffins_left === 1 ? "" : "s"} left — your plan is about to expire.`}
          </span>
        </div>
      )}

      {/* Outstanding payment nudge (online — settle now) */}
      {onlinePending.length > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-warning/30 bg-warning-bg px-4 py-3" data-testid="customer-home-unpaid-banner">
          <div className="flex items-center gap-2 text-sm text-warning">
            <Wallet size={16} strokeWidth={1.75} className="shrink-0" />
            <span className="font-medium">
              {onlinePending.length === 1
                ? `You have an outstanding payment of ${formatCurrency(onlinePending.reduce((s, p) => s + breakdownFromPayment(p, taxCfg).total, 0))}.`
                : `You have ${onlinePending.length} outstanding payments totalling ${formatCurrency(onlinePending.reduce((s, p) => s + breakdownFromPayment(p, taxCfg).total, 0))}.`}
            </span>
          </div>
          <Button size="sm" variant="outline" className="shrink-0 bg-surface" onClick={() => navigate("/customer/payments")} data-testid="customer-home-settle-button">
            Pay now
          </Button>
        </div>
      )}

      {/* Cash collection pending (driver collects on delivery — no action needed) */}
      {cashPending.length > 0 && (
        <div className="flex items-center gap-2 rounded-2xl border border-primary/25 bg-primary/5 px-4 py-3 text-sm text-primary" data-testid="customer-home-cash-pending-banner">
          <Banknote size={16} strokeWidth={1.75} className="shrink-0" />
          <span className="font-medium">
            Cash collection pending — our driver will collect it when your tiffin is delivered.
          </span>
        </div>
      )}

      {/* Thumb-friendly action grid */}
      <div className="flex gap-3">
        {pauseBatch?.has_pause ? (
          <button
            type="button" onClick={() => setPauseDialog("resume")}
            className="flex h-16 flex-1 flex-col items-center justify-center gap-1 rounded-xl border border-border bg-surface font-medium text-text-main shadow-sm transition-all active:scale-95 disabled:opacity-60"
            data-testid="customer-home-resume-button"
          >
            <Play size={18} strokeWidth={1.5} /> <span className="text-xs">Cancel pause</span>
          </button>
        ) : (
          <button
            type="button" disabled={busy === "pause"} onClick={() => setConfirmPauseTomorrow(true)}
            className="flex h-16 flex-1 flex-col items-center justify-center gap-1 rounded-xl border border-border bg-surface font-medium text-text-main shadow-sm transition-all active:scale-95 disabled:opacity-60"
            data-testid="customer-home-pause-tomorrow-button"
          >
            <PauseCircle size={18} strokeWidth={1.5} /> <span className="text-xs">Pause tomorrow</span>
          </button>
        )}
        {renewEligible && (
          <button
            type="button" disabled={busy === "recharge"} onClick={recharge}
            className="flex h-16 flex-1 flex-col items-center justify-center gap-1 rounded-xl bg-accent font-bold text-primary shadow-md transition-all active:scale-95 disabled:opacity-60"
            data-testid="customer-home-recharge-button"
          >
            <Wallet size={18} strokeWidth={1.5} /> <span className="text-xs">Renew now</span>
          </button>
        )}
      </div>

      {!pauseBatch?.has_pause && (
        <button
          type="button" onClick={() => setPauseDialog("pause")}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-surface py-3 text-sm font-medium text-text-main shadow-sm transition-all active:scale-[0.98]"
          data-testid="customer-home-pause-dates-button"
        >
          <CalendarRange size={16} strokeWidth={1.5} /> Pause for specific dates
        </button>
      )}

      {me.type === "1" && (
        <button
          type="button" onClick={() => navigate("/customer/qr")}
          className="flex w-full items-center justify-between rounded-2xl border border-border bg-surface p-4 text-left shadow-sm transition-all active:scale-[0.98]"
          data-testid="customer-home-qr-teaser"
        >
          <span className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
              <QrCode size={18} strokeWidth={1.5} />
            </span>
            <span>
              <span className="block text-sm font-semibold text-text-main">Your Pickup QR Code</span>
              <span className="block text-xs text-text-muted">Tap to view or regenerate</span>
            </span>
          </span>
          <ChevronRight size={18} strokeWidth={1.5} className="text-text-muted" />
        </button>
      )}

      {me.type === "2" && (
        <div className="rounded-2xl border border-border bg-surface p-4 shadow-sm" data-testid="customer-home-delivery-card">
          <p className="mb-2 text-sm font-semibold text-text-main">Today's Delivery</p>
          {today?.has_delivery ? (
            <>
              <p className="text-sm text-text-main" data-testid="customer-home-delivery-eta">
                {today.status_label === "Delivered" ? "Delivered" : today.eta_label || "Waiting to be dispatched"}
              </p>
              {today.route_started && <p className="mt-1 flex items-center gap-1 text-xs text-text-muted"><MapPin size={12} strokeWidth={1.5} /> Driver is out on route</p>}
            </>
          ) : (
            <p className="text-sm text-text-muted" data-testid="customer-home-delivery-none">No delivery scheduled for today.</p>
          )}
          <button
            type="button" onClick={() => navigate("/customer/history")}
            className="mt-3 flex items-center gap-1.5 text-xs font-medium text-text-muted"
            data-testid="customer-home-history-link"
          >
            <History size={13} strokeWidth={1.5} /> View delivery history
          </button>
        </div>
      )}

      <ConfirmDialog
        open={renewEligible && !expiryDismissed}
        onOpenChange={(v) => !v && setExpiryDismissed(true)}
        title={me.status === 4 ? "Your plan has expired" : "Your plan is about to expire"}
        description={me.status === 4
          ? "Renew now to start a fresh plan and keep your tiffins coming."
          : `You have only ${me.tiffins_left} tiffin${me.tiffins_left === 1 ? "" : "s"} left. Renew now to avoid any interruption to your deliveries.`}
        confirmLabel="Renew now"
        cancelLabel="Not now"
        variant="accent"
        loading={busy === "recharge"}
        onConfirm={recharge}
        testId="customer-expiry-renew-dialog"
      />
      <ConfirmDialog
        open={confirmPauseTomorrow}
        onOpenChange={setConfirmPauseTomorrow}
        title="Pause tomorrow's delivery?"
        description="Tomorrow's tiffin will be skipped and added back to the end of your plan. You can cancel this pause later."
        confirmLabel="Pause Tomorrow"
        variant="accent"
        loading={busy === "pause"}
        onConfirm={pauseTomorrow}
        testId="customer-pause-tomorrow-confirm"
      />
      <PauseResumeDialog
        open={pauseDialog === "pause" || pauseDialog === "resume"}
        onOpenChange={(v) => setPauseDialog(v ? pauseDialog : null)}
        mode={pauseDialog === "resume" ? "resume" : "pause"}
        pauseUrl="/customer/pause"
        resumeUrl="/customer/resume-pause"
        batchUrl="/customer/pause-batch"
        pauseMinDate={tomorrowStr()}
        onDone={() => { setPauseDialog(null); load(); }}
      />
    </div>
  );
}

function tomorrowStr() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}
